import * as React from "react";
import warning from "tiny-warning";

import { RouterProps, Route } from "@core-ui/interfaces/Router";
import { RouteContext, RouteContextValue } from "@core-ui/contexts/RouteContext";
import { RouteStack, findRouteByName, buildRoutes, getRouteElement, addToStack } from "./Utils";
import { useEffectOnce } from "@core-ui/hooks/UseEffectOnce";
import { ScreenTransition } from "@core-ui/components/screen-transition";

export function Router<T extends string = string>(props: RouterProps<T>): React.ReactElement | null {
    const { children, externalRoutes = [], nonOverlappingTransitionRoutes = [] } = props;
    const routes = React.useMemo(() => buildRoutes([...children, ...externalRoutes]), [children, externalRoutes]);

    const [routeState, setRouteState] = React.useState<{
        forward: boolean;
        activeRoute: Route<T> | undefined;
        routeStack: RouteStack<T>;
    }>(() => {
        const route = findRouteByName(routes, props.initialRoute.name);
        const activeRoute = route ? { ...route, data: props.initialRoute.data } : route;

        return {
            forward: true,
            activeRoute,
            routeStack: activeRoute ? [{ name: activeRoute.name, data: activeRoute.data }] : [],
        };
    });

    const shouldUseNonOverlappingTransition = React.useMemo(() => {
        return routeState.activeRoute?.name && nonOverlappingTransitionRoutes.includes(routeState.activeRoute.name);
    }, [routeState.activeRoute?.name, nonOverlappingTransitionRoutes]);

    const canGoBack = React.useCallback(
        (numberOfPagesToGoBack = 1): boolean => {
            return routeState?.routeStack.length > numberOfPagesToGoBack;
        },
        [routeState],
    );

    const navigateTo = React.useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (routeName: T, data?: any, replace?: boolean, errorCallback?: VoidFunction) => {
            const route = findRouteByName(routes, routeName, routeState.activeRoute?.name);
            if (!route) {
                warning(false, `invalid route ${routeName} specified`);
                errorCallback?.();
                return;
            }

            const [stack, forward] = addToStack({
                replace,
                data,
                name: routeName,
                stack: routeState.routeStack,
            });

            setRouteState({
                forward,
                activeRoute: { ...route, data },
                routeStack: stack,
            });
        },
        [routes, routeState.activeRoute?.name, routeState.routeStack],
    );

    const goBack = React.useCallback((): void => {
        if (!canGoBack()) {
            return;
        }

        const entry = routeState.routeStack[routeState.routeStack.length - 2];
        if (!entry) {
            return;
        }

        navigateTo(entry.name, entry.data);
    }, [canGoBack, navigateTo, routeState?.routeStack]);

    const contextValue = React.useMemo<RouteContextValue<T>>(
        () => ({ navigateTo, goBack, canGoBack, route: routeState.activeRoute, isForward: routeState.forward }),
        [canGoBack, goBack, navigateTo, routeState.activeRoute, routeState.forward],
    ) as unknown;

    useEffectOnce(() => {
        history.pushState(
            { name: props.initialRoute.name ?? "show-payments", data: props.initialRoute.data ?? undefined },
            "",
            "",
        );
    });

    const Container = props.container || React.Fragment;

    return (
        <RouteContext.Provider value={contextValue as RouteContextValue<string>}>
            <Container>
                <ScreenTransition
                    transitionMode={shouldUseNonOverlappingTransition ? "out-in" : "in-out"}
                    forward={routeState.forward}
                >
                    {getRouteElement(routes, routeState.activeRoute) ?? <div key="no-matching-element"></div>}
                </ScreenTransition>
            </Container>
        </RouteContext.Provider>
    );
}
